import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Tag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPromoCodes() {
    const { getAuthHeaders } = useAuth();
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingCode, setEditingCode] = useState(null);
    const [formData, setFormData] = useState({
        code: '',
        discount_percent: '',
        free_trial_days: '',
        max_uses: '',
        expires_at: ''
    });

    const fetchCodes = async () => {
        try {
            const response = await axios.get(`${API}/admin/promo-codes`, {
                headers: getAuthHeaders()
            });
            setCodes(response.data);
        } catch (error) {
            console.error('Failed to fetch promo codes:', error);
            toast.error('Nepodařilo se načíst promo kódy');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCodes();
    }, [getAuthHeaders]);

    const openCreateDialog = () => {
        setEditingCode(null);
        setFormData({
            code: '',
            discount_percent: '',
            free_trial_days: '',
            max_uses: '',
            expires_at: ''
        });
        setDialogOpen(true);
    };

    const openEditDialog = (code) => {
        setEditingCode(code);
        setFormData({
            code: code.code,
            discount_percent: code.discount_percent?.toString() || '',
            free_trial_days: code.free_trial_days?.toString() || '',
            max_uses: code.max_uses?.toString() || '',
            expires_at: code.expires_at?.split('T')[0] || ''
        });
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const payload = {
            code: formData.code,
            discount_percent: formData.discount_percent ? parseInt(formData.discount_percent) : null,
            free_trial_days: formData.free_trial_days ? parseInt(formData.free_trial_days) : null,
            max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
            expires_at: formData.expires_at || null
        };
        
        try {
            if (editingCode) {
                await axios.put(`${API}/admin/promo-codes/${editingCode.id}`, payload, {
                    headers: getAuthHeaders()
                });
                toast.success('Promo kód byl upraven');
            } else {
                await axios.post(`${API}/admin/promo-codes`, payload, {
                    headers: getAuthHeaders()
                });
                toast.success('Promo kód byl vytvořen');
            }
            setDialogOpen(false);
            fetchCodes();
        } catch (error) {
            const message = error.response?.data?.detail || 'Nepodařilo se uložit promo kód';
            toast.error(message);
        }
    };

    const toggleCodeStatus = async (code) => {
        try {
            await axios.put(`${API}/admin/promo-codes/${code.id}`, {
                is_active: !code.is_active
            }, {
                headers: getAuthHeaders()
            });
            toast.success(code.is_active ? 'Promo kód deaktivován' : 'Promo kód aktivován');
            fetchCodes();
        } catch (error) {
            toast.error('Nepodařilo se změnit stav kódu');
        }
    };

    return (
        <div data-testid="admin-promo-codes">
            <div className="flex items-center justify-between mb-8">
                <h1 className="font-heading text-2xl md:text-3xl font-semibold">
                    Promo kódy
                </h1>
                <Button onClick={openCreateDialog} className="listarna-btn-primary" data-testid="create-promo-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nový kód
                </Button>
            </div>

            {/* Promo Codes Table */}
            <div className="listarna-card rounded-lg overflow-hidden">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Kód</th>
                            <th>Sleva</th>
                            <th>Trial</th>
                            <th>Použití</th>
                            <th>Platnost</th>
                            <th>Stav</th>
                            <th className="text-right">Akce</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={7} className="text-center py-8">
                                    <div className="animate-pulse">Načítání...</div>
                                </td>
                            </tr>
                        ) : codes.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="text-center py-8 text-muted-foreground">
                                    Žádné promo kódy
                                </td>
                            </tr>
                        ) : (
                            codes.map((code) => (
                                <tr key={code.id} data-testid={`promo-row-${code.id}`}>
                                    <td className="font-mono font-medium">{code.code}</td>
                                    <td>
                                        {code.discount_percent ? `${code.discount_percent}%` : '-'}
                                    </td>
                                    <td>
                                        {code.free_trial_days ? `${code.free_trial_days} dní` : '-'}
                                    </td>
                                    <td>
                                        {code.uses_count}{code.max_uses ? `/${code.max_uses}` : ''}
                                    </td>
                                    <td className="text-muted-foreground">
                                        {code.expires_at 
                                            ? new Date(code.expires_at).toLocaleDateString('cs-CZ')
                                            : 'Neomezená'}
                                    </td>
                                    <td>
                                        <span className={`px-2 py-1 rounded-full text-xs ${
                                            code.is_active 
                                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                : 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
                                        }`}>
                                            {code.is_active ? 'Aktivní' : 'Neaktivní'}
                                        </span>
                                    </td>
                                    <td className="text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => openEditDialog(code)}
                                                className="p-2 hover:bg-secondary rounded-lg"
                                                title="Upravit"
                                                data-testid={`edit-promo-${code.id}`}
                                            >
                                                <Edit className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => toggleCodeStatus(code)}
                                                className="p-2 hover:bg-secondary rounded-lg"
                                                title={code.is_active ? 'Deaktivovat' : 'Aktivovat'}
                                                data-testid={`toggle-promo-${code.id}`}
                                            >
                                                <Tag className={`w-4 h-4 ${code.is_active ? 'text-green-500' : 'text-gray-400'}`} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            {editingCode ? 'Upravit promo kód' : 'Nový promo kód'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                        <div>
                            <Label htmlFor="code">Kód</Label>
                            <Input
                                id="code"
                                value={formData.code}
                                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                                required
                                disabled={!!editingCode}
                                placeholder="LETO2024"
                                className="font-mono"
                                data-testid="promo-code-input"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="discount_percent">Sleva (%)</Label>
                                <Input
                                    id="discount_percent"
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={formData.discount_percent}
                                    onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })}
                                    placeholder="20"
                                    data-testid="promo-discount-input"
                                />
                            </div>
                            <div>
                                <Label htmlFor="free_trial_days">Trial (dny)</Label>
                                <Input
                                    id="free_trial_days"
                                    type="number"
                                    min="0"
                                    value={formData.free_trial_days}
                                    onChange={(e) => setFormData({ ...formData, free_trial_days: e.target.value })}
                                    placeholder="30"
                                    data-testid="promo-trial-input"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="max_uses">Max. použití</Label>
                                <Input
                                    id="max_uses"
                                    type="number"
                                    min="0"
                                    value={formData.max_uses}
                                    onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                                    placeholder="Neomezeno"
                                    data-testid="promo-maxuses-input"
                                />
                            </div>
                            <div>
                                <Label htmlFor="expires_at">Platnost do</Label>
                                <Input
                                    id="expires_at"
                                    type="date"
                                    value={formData.expires_at}
                                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                                    data-testid="promo-expires-input"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-4 pt-4">
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Zrušit
                            </Button>
                            <Button type="submit" className="listarna-btn-primary" data-testid="promo-submit-btn">
                                {editingCode ? 'Uložit změny' : 'Vytvořit'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
